import { useMediaQuery, useTheme } from '@mui/material';
import { TAGBackground } from '@aspendental/themes';

import {
	BlogCategory,
	BlogDetailsPageSummary,
	BlogDetailsPageText,
	BlogSubCategory,
	ImageAsset as TImageAsset,
	Maybe,
	BlogDetailsPageHeroButtonCollection,
	BlogDetailsPageHeroContentCollection,
	BlogDetailsPageHeroContentTitle,
	BlogDetailsPageAdditionalContentCollection,
	ContentSection,
	Brand,
} from '@/types/generated';
import { BrandFolderImage, FocalPointData } from '@/components/FocalPointImage';
import { ContentfulBackground } from '@/types';

import { IBlogAuthorProps } from './BlogAuthorDetails';
import BlogDetailsPageFull from './BlogDetailsPageFull';
import BlogDetailsPageStandard from './BLogDetailsPageStandard';

export interface BlogImage {
	image: BrandFolderImage;
	focalPoint: FocalPointData;
}

export type TBlogPageDesignType = Maybe<'Standard' | 'Full Page'>;

export interface IBlogDetailsPage {
	/**
	 * Required field for the BlogDetailsPage title
	 *
	 * @type {string}
	 * @memberof IBlogDetailsPage
	 */
	title: string;
	/**
	 * Required field for the BlogDetailsPage path
	 *
	 * @type {string}
	 * @memberof IBlogDetailsPage
	 */
	path: string;
	/**
	 * Required field to identify the date of the blog
	 *
	 * @type {string}
	 * @memberof IBlogDetailsPage
	 */
	date: string;
	/**
	 * Required field to identify the category of the blog.
	 *
	 * @type {Maybe<BlogCategory>}
	 * @memberof IBlogDetailsPage
	 */
	category: Maybe<BlogCategory>;
	/**
	 * Required field to identify the category of the blog.
	 *
	 * @type {Maybe<BlogSubCategory>}
	 * @memberof IBlogDetailsPage
	 */
	subCategory: Maybe<BlogSubCategory>;
	/**
	 * Required field to identify the time to read for the blog
	 *
	 * @type {number}
	 * @memberof IBlogDetailsPage
	 */
	timeToRead: number;
	/**
	 * Required field to identify the blog summary
	 *
	 * @type {Maybe<BlogDetailsPageSummary>;}
	 * @memberof IBlogDetailsPage
	 */
	summary: Maybe<BlogDetailsPageSummary>;
	/**
	 * Required field to identify the blog text
	 *
	 * @type {Maybe<BlogDetailsPageText>;}
	 * @memberof IBlogDetailsPage
	 */
	text: Maybe<BlogDetailsPageText>;
	/**
	 * Required field to identify the blog image
	 *
	 * @type {Array<IBlogDetailPageImage>;}
	 * @memberof IBlogDetailsPage
	 */
	blogImage: BlogImage;
	/**
	 * Optional field to identify the blog author
	 *
	 * @type {IBlogAuthorProps;}
	 * @memberof IBlogDetailsPage
	 */
	author?: IBlogAuthorProps;
	/**
	 * Optional field to identify the blog reviewer
	 *
	 * @type {IBlogAuthorProps;}
	 * @memberof IBlogDetailsPage
	 */
	reviewer?: IBlogAuthorProps;
	/**
	 * Brandfolder Image with focal point.
	 *
	 * @type {Maybe<ImageAsset>}
	 * @memberof IBlogDetailsPage
	 */
	imageAsset?: Maybe<TImageAsset>;
	/**
	 * Enables a blog hero section above content.
	 *
	 * @type {Maybe<TBlogPageDesignType>}
	 * @memberof IBlogDetailsPage
	 */
	pageDesignType?: TBlogPageDesignType;
	heroBackgroundColor?: Maybe<ContentfulBackground | TAGBackground>;
	/**
	 * The title for the content display.
	 *
	 * @type {Maybe<BlogDetailsPageHeroContentCollection>}
	 * @memberof IBlogDetailsPage
	 */
	heroContentTitle?: Maybe<BlogDetailsPageHeroContentTitle>;
	/**
	 * The collection of content display (currently richtext only). Maximum of 3 items(s).
	 *
	 * @type {Maybe<BlogDetailsPageHeroContentCollection>}
	 * @memberof IBlogDetailsPage
	 */
	heroContentCollection?: Maybe<BlogDetailsPageHeroContentCollection>;
	/**
	 * The collection of buttons to display. Maximum of 1 button(s).
	 *
	 * @type {Maybe<BlogDetailsPageHeroButtonCollection>}
	 * @memberof IBlogDetailsPage
	 */
	heroButtonCollection?: Maybe<BlogDetailsPageHeroButtonCollection>;
	/**
	 * The section of title and description for the related blogs. Will display the related blogs if they are present.
	 *
	 * @type {Maybe<ContentSection>}
	 * @memberof IBlogDetailsPage
	 */
	relatedBlogsContentSection?: Maybe<ContentSection>;
	/**
	 * The collection of components to display. Maximum of 2 item(s). Supports: SingleMessage, Accordion.
	 *
	 * @type {Maybe<BlogDetailsPageAdditionalContentCollection>}
	 * @memberof IBlogDetailsPage
	 */
	additionalContentCollection?: Maybe<BlogDetailsPageAdditionalContentCollection>;
	preview?: boolean;
	/**
	 * The brand to which the blog post belongs: Aspen Dental, ClearChoice, WellNow, or Lovet.
	 *
	 * @type {Maybe<Brand>}
	 * @memberof IBlogDetailsPage
	 */
	brand?: Maybe<Brand>;

	tags?: Array<string>;
}

export default function BlogDetailsPage(props: Readonly<IBlogDetailsPage>) {
	const {
		pageDesignType,
		heroBackgroundColor,
		heroButtonCollection,
		heroContentCollection,
		relatedBlogsContentSection,
		additionalContentCollection,
		preview,
		brand,
		tags,
	} = props;

	const theme = useTheme();

	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	if (pageDesignType === 'Full Page') {
		return (
			<BlogDetailsPageFull
				{...props}
				isSmallScreen={isSmallScreen}
				theme={theme}
				heroBackgroundColor={heroBackgroundColor}
				heroButtonCollection={heroButtonCollection}
				heroContentCollection={heroContentCollection}
				relatedBlogsContentSection={relatedBlogsContentSection}
				additionalContentCollection={additionalContentCollection}
				preview={preview}
				brand={brand}
			/>
		);
	}

	return <BlogDetailsPageStandard {...props} isSmallScreen={isSmallScreen} theme={theme} tags={tags} />;
}
